// Copyright (c) Docugami, Inc. All rights reserved.

import { IDragDropEvents } from "@fluentui/react";
import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { getDirectoryPath, isDirectory } from "components/file_management/file_helpers";
import { DocumentCommandBar } from "components/file_management/presentation/DocumentCommandBar";
import { ListViewColumns } from "components/file_management/presentation/DocumentListView";
import { directoryToViewItem, DocumentViewer, DocumentViewerProps, fileToViewItem, IDocumentViewControls, IDocumentViewItem } from "components/file_management/presentation/DocumentViewer";
import { FileSystemBreadCrumbs } from "components/file_management/presentation/FileSystemBreadCrumbs";
import { useFileUploader } from "components/file_management/presentation/FileUploader";
import { DgDirectory, DgFile } from "filemanager/FileManager";
import { getFileStatusInfo } from "filemanager/FileStatus";
import { useAssignDocSetCommand } from "hooks/AssignDocSetCommand";
import { useDeleteFilesCommand } from "hooks/DeleteFilesCommand";
import { useDownloadFilesCommand } from "hooks/DownloadFilesCommand";
import { useFilePreview } from "hooks/FilePreview";
import { useFileViewCoachMarks as useFileSystemCoachMarks } from "hooks/FileViewCoachMarks";
import { useDirectoryView } from "hooks/FileViews";
import { useNavigationService } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { ProviderId } from "networking/StorageProviders";
import { useEffect, useMemo, useRef, useState } from "react";
import { getProviderFriendlyName, getProviderRootPath } from "storage/StorageProviderHelpers";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import styled from "styled-components";
import { DocSourceId } from "./ExplorerTypes";
import { ExplorerView } from "./ExplorerView";

const DocumentViewerContainer = styled.div`
    width: 100%;
    height: 100%;
`

interface FileSystemExplorerViewProps {
    path: string;
    rootPath: string;
    label: string;
    telemetryId: string;
    onPathChanged: (path: string) => void;
    showHidden: boolean;
    dragDropEvents?: IDragDropEvents;
    commands: IDocumentCommand[];
    selectedItems: string[];
    setSelectedItems: (items: string[]) => void;
    providerId?: ProviderId;
}

export const FileSystemExplorerView = (props: FileSystemExplorerViewProps): JSX.Element => {
    const viewRef = useRef<IDocumentViewControls>(null);
    const filter: ListViewColumns = ListViewColumns.Icon | ListViewColumns.Name | ListViewColumns.DocSet | ListViewColumns.DocStatus | ListViewColumns.AddedOn | ListViewColumns.Size;
    const docSets = useAppSelector(state => state.clusters.indexed);
    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles);
    const { navToAddDocsPage, getDeepLinkToDocSetPage } = useNavigationService();

    const { selectedItems, setSelectedItems, commands } = props;

    const { isLoaded, items } = useDirectoryView(props.path, props.showHidden);

    const documentViewItems = useMemo(() => {
        const viewItems: IDocumentViewItem[] = [];
        items.forEach(item => {
            if (item instanceof DgFile) {
                viewItems.push(
                    fileToViewItem(
                        item.properties,
                        getFileStatusInfo(item.properties, docSets[item.properties.clusterId]?.clusterInfo, docSetDocThreshold),
                        false,
                        props.providerId)
                )
            }
            else {
                viewItems.push(directoryToViewItem((item as DgDirectory), props.providerId));
            }
        })
        return viewItems.sort(sortItems);
    }, [items, docSets, props.providerId, docSetDocThreshold]);

    const documentIds = useMemo(() =>
        documentViewItems.reduce<string[]>((ids, viewItem) => !isDirectory(viewItem.id) ? [...ids, viewItem.folderId] : ids, []), [documentViewItems])

    const { filePreview, setPreviewFile, canPreview, previewCommands } = useFilePreview(documentIds);

    const coachMark = useFileSystemCoachMarks(props.rootPath === props.path, navToAddDocsPage, isLoaded ? items.length : undefined);
    const overlays = useMemo(() => [coachMark, ...commands.filter(c => c.isActive && !!c.visual).map(x => x.visual) as JSX.Element[]], [commands, coachMark]);

    useEffect(() => {
        if (!filePreview) {
            viewRef.current?.selectItems(selectedItems);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [filePreview]);

    const documentViewProps: DocumentViewerProps = {
        items: documentViewItems,
        selectedItems: selectedItems,
        filter: filter,
        onSelectedItemsChanged: items => setSelectedItems(items),
        actions: {
            name: item => {
                if (isDirectory(item.id)) {
                    props.onPathChanged(item.id)
                }
                else if (canPreview(item.folderId)) {
                    setPreviewFile(item.folderId);
                }
            }
        },
        links: {
            docSet: item => getDeepLinkToDocSetPage(item.docSet) ?? ""
        },
        validateActions: {
            name: item => isDirectory(item.id) || canPreview(item.folderId),
            docSet: item => !!item.docSet
        },
        dragDropEvents: props.dragDropEvents
    };

    const header = <DocumentCommandBar commands={!!filePreview ? previewCommands : commands} />

    const content = !!filePreview ? filePreview : (
        <DocumentViewerContainer>
            <DocumentViewer ref={viewRef} {...documentViewProps} />
        </DocumentViewerContainer>
    );

    const footer = <FileSystemBreadCrumbs path={props.path} rootLabel={props.label} rootPath={props.rootPath} onClick={p => props.onPathChanged(p)} />

    return (
        <ExplorerView
            header={header}
            content={content}
            overlays={!!filePreview ? previewCommands.map(x => x.visual) : overlays}
            footer={!!filePreview ? null : footer}
            telemetryId={props.telemetryId} />
    );
}

interface LocalFileSystemExplorerViewProps {
    rootPath?: string;
}

export const LocalFileSystemExplorerView = (props: LocalFileSystemExplorerViewProps): JSX.Element => {
    const [path, setPath] = useState(props.rootPath ?? '')
    const [, dropZone] = useFileUploader(path);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const deleteFilesCommand = useDeleteFilesCommand(selectedItems, () => { });
    const assignCommand = useAssignDocSetCommand("Assign to", selectedItems, () => { });
    const downloadCommand = useDownloadFilesCommand(selectedItems, () => { });

    const commands = useMemo(() => [assignCommand, downloadCommand, deleteFilesCommand], [assignCommand, downloadCommand, deleteFilesCommand]);

    useEffect(() => {
        setPath(props.rootPath ?? '');
    }, [props.rootPath]);

    const dragDropEvents: IDragDropEvents = {
        canDrop: () => true,
        onDragEnter: (item, event) => {
            const isDroppable = !!event && !!item && !item.size;
            if (isDroppable) {
                event?.stopPropagation();
                event?.preventDefault();
                return "file-system-drag-enter";
            }

            return "";
        },
        onDrop: (item?: IDocumentViewItem, event?: DragEvent | undefined) => {
            const isDroppable = !!item && !item.size;
            if (!isDroppable || !event?.dataTransfer) {
                return;
            }

            event.stopPropagation();
            event.preventDefault();
            const rootPath = !!item ? getDirectoryPath(item.id) : path;
            dropZone.onDataTransfer(event.dataTransfer, rootPath)
        }
    }

    return (
        <DocumentViewerContainer
            onDragOver={dropZone.onDragOver}
            onDrop={dropZone.onDrop}
            onDragEnter={dropZone.onDragEnter}>
            <FileSystemExplorerView
                rootPath={props.rootPath ?? ''}
                path={path}
                label="Local Docs"
                telemetryId={DocSourceId.Docugami}
                onPathChanged={p => setPath(p)}
                dragDropEvents={dragDropEvents}
                commands={commands}
                showHidden={false}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems} />
        </DocumentViewerContainer>
    )
}

interface RemoteFileSystemExplorerViewProps {
    providerId: ProviderId;
}

export const RemoteFileSystemExplorerView = (props: RemoteFileSystemExplorerViewProps): JSX.Element => {
    const rootPath = useMemo(() => getProviderRootPath(props.providerId), [props.providerId]);
    const [path, setPath] = useState(rootPath);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const deleteFilesCommand = useDeleteFilesCommand(selectedItems, () => { });
    const assignCommand = useAssignDocSetCommand("Assign to", selectedItems, () => { });
    const downloadCommand = useDownloadFilesCommand(selectedItems, () => { });

    const commands = useMemo(() => [assignCommand, downloadCommand, deleteFilesCommand], [assignCommand, downloadCommand, deleteFilesCommand]);

    useEffect(() => {
        setPath(rootPath);
    }, [rootPath]);

    return (
        <FileSystemExplorerView
            rootPath={rootPath}
            path={path}
            label={getProviderFriendlyName(props.providerId)}
            telemetryId={props.providerId}
            onPathChanged={p => setPath(p)}
            commands={commands}
            showHidden={false}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            providerId={props.providerId} />
    )
}

function sortItems(item1: IDocumentViewItem, item2: IDocumentViewItem): number {
    const isItem1Directory = isDirectory(item1.id);
    const isItem2Directory = isDirectory(item2.id);
    if (isItem1Directory !== isItem2Directory) {
        return isItem1Directory ? -1 : 1;
    }
    else if (item1.name === item2.name) {
        return 0;
    }
    else {
        return item1.name < item2.name ? -1 : 1;
    }
}
